// External libraries
import React from 'react';
import { func, string } from 'prop-types';

// Internal modules
import {
  streamlineGuestTermsEmbeddedLinks,
  streamlineSignupTermsEmbeddedLinks,
} from './streamlineTermsEmbeddedLinks';
import {
  features,
  getContent,
  getLocale,
  hasSelectAllSignupTerms,
} from '@/lib';
import { useMountLogging } from '@/lib/reactHooks';
import { getOnboardTermsStreamlineKey } from '../utils';
import { SectionSubHeading } from '@/components/Headings';
import { StyledOnboardTermsText } from '../StyledComponents';
import { OnboardTermsWithCheckbox } from '../OnboardTermsWithCheckbox';
import { STREAMLINE_COUNTRIES_WITH_CHECKBOX_TERMS } from '@/lib/constants';
import { PasswordlessTooltip } from '@/components/PasswordlessOnboarding/Components';
import { LazySelectAllTerms } from '../../SignupFields/SelectAllTerms';
import { LazyPrivacyStatement } from '../../SignupFields/LegalDisclosures/Components';
import { FONT_SIZE_SMALL, LABEL_COLOR } from '@/styles/styleVariables';
import type { AppMode } from '@/types';

interface StreamlineOnboardTermsProps {
  changeHandler: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isPasswordless: boolean | undefined;
  type: AppMode;
}

const StreamlineOnboardTerms = ({
  changeHandler,
  isPasswordless,
  type,
}: StreamlineOnboardTermsProps): JSX.Element => {
  useMountLogging(`${type}OnboardTerms`);
  const hasSelectAllExp = hasSelectAllSignupTerms();
  const contentString = getOnboardTermsStreamlineKey({
    isPasswordless,
    type,
  });

  if (type === 'guest') {
    // Compliance: Domestic guest transactions in JP require UA acceptance with a checkbox
    const isShowCheckboxGuestFlow = features.isActive(
      'shouldShowDomesticUserAgreementAcceptance',
    );

    if (isShowCheckboxGuestFlow) {
      return (
        <OnboardTermsWithCheckbox
          type="guest"
          changeHandler={changeHandler}
          label={getContent(contentString, streamlineGuestTermsEmbeddedLinks())}
          errorContentKey="fieldErrors.common.valueMissing"
        />
      );
    }

    return (
      <StyledOnboardTermsText data-testid="streamline-guest-terms">
        {getContent(contentString, streamlineGuestTermsEmbeddedLinks())}
      </StyledOnboardTermsText>
    );
  }

  const signupContent = getContent(
    contentString,
    streamlineSignupTermsEmbeddedLinks(),
  );

  // Show T&C checkbox for streamline flow in required countries
  const isShowCheckbox = STREAMLINE_COUNTRIES_WITH_CHECKBOX_TERMS.includes(
    getLocale()?.country,
  );

  if (isShowCheckbox) {
    return (
      <>
        {hasSelectAllExp && <LazySelectAllTerms />}

        <OnboardTermsWithCheckbox
          type={type}
          changeHandler={changeHandler}
          label={signupContent}
          errorContentKey={
            hasSelectAllExp ? 'fieldErrors.common.valueMissing' : undefined
          }
        />

        {features.isActive('hasPrivacyStatementTerms') && (
          <LazyPrivacyStatement changeHandler={changeHandler} />
        )}
      </>
    );
  }

  if (isPasswordless) {
    return (
      <SectionSubHeading
        color={LABEL_COLOR}
        fontSize={FONT_SIZE_SMALL}
        fontWeight={700}
        data-testid="streamline-signup-terms-passwordless"
      >
        <PasswordlessTooltip content={signupContent} />
      </SectionSubHeading>
    );
  }

  return (
    <StyledOnboardTermsText data-testid="streamline-signup-terms-password">
      {signupContent}
    </StyledOnboardTermsText>
  );
};

StreamlineOnboardTerms.propTypes = {
  changeHandler: func.isRequired,
  isPasswordless: string.isRequired,
  type: string.isRequired,
};

export { StreamlineOnboardTerms };
